/**
 * @file Input.tsx
 * @brief Plain text-input primitive matching shadcn/ui visual conventions.
 *
 * @details
 * Inputs are **full pills** (`rounded-full`) to match the mobile app's
 * source of truth — `borderRadius: 24` on `height: 48` (= 50% = full
 * pill) in `FRONTEND/src/screens/CompanyAdmin/components/UserEditModal.tsx`,
 * and `borderRadius: 34` on `height: 68` in the auth pills. We keep
 * `h-10` (40px) which becomes a 20-px pill at `rounded-full`, with
 * `px-5` so the pill ends don't crowd the text.
 *
 * 01-10-2026: inside a `FormFieldGroup` (and with `BARE_FIELDS` on) the
 * input is the app's bare field instead, its label as placeholder; see
 * `fieldStyle.tsx`.
 *
 * @author DES
 * @date 26-05-2026
 */

import { forwardRef, type FocusEvent, type InputHTMLAttributes } from "react";
import { cn } from "@/shared/theme/cn";
import { capitalizeFirst as firstCapital } from "@/shared/utils/capitalizeFirst";
import { bareControlClass, useFieldStyle } from "./fieldStyle";

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /**
   * The first letter is a capital (DES 12-10-2026, "pehlay word", the
   * phone's rule; 11-10 had Every Word). Phone and iPad keyboards do it as
   * you type (`autoCapitalize`); a desktop keyboard never does, so the value
   * is fixed when the field is LEFT, only ever adding the one capital
   * (capitalizeFirst.ts).
   */
  capitalizeFirst?: boolean;
};

/** Sets a field's value the way typing does, so React's onChange and
 *  react-hook-form both see it. */
function setTypedValue(el: HTMLInputElement, value: string) {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, placeholder, capitalizeFirst, onBlur, autoCapitalize, ...props }, ref) => {
    const field = useFieldStyle();
    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (capitalizeFirst) {
        const fixed = firstCapital(e.currentTarget.value);
        if (fixed !== e.currentTarget.value) setTypedValue(e.currentTarget, fixed);
      }
      onBlur?.(e);
    };
    return (
      <input
        type={type}
        ref={ref}
        autoCapitalize={autoCapitalize ?? (capitalizeFirst ? "sentences" : undefined)}
        onBlur={handleBlur}
        className={cn(
          "flex h-10 w-full rounded-full border border-input bg-field px-5 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          field.bare && bareControlClass,
          field.bare && field.error && "placeholder:!text-destructive",
          className,
        )}
        // Bare: the label is the placeholder, and the error replaces it.
        placeholder={field.bare && (field.error || field.label) ? field.error || field.label : placeholder}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";
